<template>
  <div id="app">
    <!-- Process Breakdown Popup -->
    <div v-if="showProcessBreakdown" class="simple-popup" @click.self="showProcessBreakdown = false">
      <div class="popup-content">
        <div class="popup-header">
          <h3>Process Type Breakdown</h3>
          <button @click="showProcessBreakdown = false" class="close-btn">×</button>
        </div>
        <div class="popup-body">
          <div class="process-breakdown-container">
            <div v-for="process in processTypeBreakdown" :key="process.processType" class="process-item">
              <div class="process-header">
                <span :class="['process-badge', getProcessTypeClass(process.processType)]">
                  {{ process.processType }}
                </span>
              </div>
              <div class="process-stats">
                <div class="stat-row">
                  <span class="stat-label">Total:</span>
                  <span class="stat-value">{{ process.totalCount }}</span>
                </div>
                <div class="stat-row">
                  <span class="stat-label">Successful:</span>
                  <span class="stat-value success">{{ process.successCount }}</span>
                </div>
                <div class="stat-row">
                  <span class="stat-label">Failed:</span>
                  <span class="stat-value error">{{ process.errorCount }}</span>
                </div>
                <div class="process-bar">
                  <div class="success-bar" :style="{ width: ((process.successCount / process.totalCount) * 100) + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Account Summary Popup -->
    <div v-if="showAccountSummary" class="simple-popup" @click.self="showAccountSummary = false">
      <div class="popup-content">
        <div class="popup-header">
          <h3>Account Summary</h3>
          <button @click="showAccountSummary = false" class="close-btn">×</button>
        </div>
        <div class="popup-body">
          <div class="account-summary-container">
            <div class="summary-section">
              <h4>Processed Accounts</h4>
              <div class="account-grid">
                <div v-for="account in parsedAccountSummary" :key="account.accountId" class="account-card">
                  <div class="account-id">Account #{{ account.accountId }}</div>
                  <div class="account-ops">
                    Total: {{ account.totalCount }} |
                    Success: {{ account.successCount }} |
                    Errors: {{ account.errorCount }}
                  </div>
                  <div class="account-time">{{ formatDateTime(account.lastProcessTime) }}</div>
                </div>
              </div>
            </div>
            <div class="summary-section" v-if="unprocessedAccounts.length > 0">
              <h4>Unprocessed Accounts</h4>
              <div class="unprocessed-list">
                <div v-for="account in unprocessedAccounts" :key="account.accountId" class="unprocessed-item">
                  <span class="account-badge">Account #{{ account.accountId }}</span>
                  <span class="device-count">{{ account.deviceCount }} devices</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Details Popup -->
    <div v-if="showErrorDetails" class="simple-popup" @click.self="showErrorDetails = false">
      <div class="popup-content">
        <div class="popup-header">
          <h3>Error Details</h3>
          <button @click="showErrorDetails = false" class="close-btn">×</button>
        </div>
        <div class="popup-body">
          <div class="error-details-container">
            <div v-for="error in errorDetails" :key="error.id" class="error-item">
              <div class="error-header">
                <span class="error-id">Log #{{ error.id }}</span>
                <span class="error-account">Account {{ error.accountId }}</span>
              </div>
              <div class="error-process">{{ error.processType }}</div>
              <div class="error-message">{{ error.message }}</div>
              <div class="error-time">{{ formatDateTime(error.processTime) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Activity Details Popup -->
    <div v-if="selectedActivity" class="simple-popup" @click.self="selectedActivity = null">
      <div class="popup-content">
        <div class="popup-header">
          <h3>Process Log Details</h3>
          <button @click="selectedActivity = null" class="close-btn">×</button>
        </div>
        <div class="popup-body">
          <div class="detail-row">
            <strong>Log ID:</strong> #{{ selectedActivity.ID }}
          </div>
          <div class="detail-row">
            <strong>Process Time:</strong> {{ formatDateTime(selectedActivity.ProcessTime) }}
          </div>
          <div class="detail-row" v-if="selectedActivity.AccountID">
            <strong>Account ID:</strong> {{ selectedActivity.AccountID }}
          </div>
          <div class="detail-row">
            <strong>Process Type:</strong>
            <span :class="['process-badge', getProcessTypeClass(selectedActivity.ProcessType)]">
              {{ selectedActivity.ProcessType }}
            </span>
          </div>
          <div class="detail-row">
            <strong>Status:</strong>
            <span :class="'status-' + selectedActivity.Status.toLowerCase()">{{ selectedActivity.Status }}</span>
          </div>
          <div class="detail-row">
            <strong>Message:</strong><br>
            <div class="message-text">{{ selectedActivity.Message }}</div>
          </div>
          <div class="detail-row">
            <strong>Description:</strong><br>
            <div class="message-text">{{ selectedActivity.Description }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Header -->
    <div class="header-toolbar">
      <div class="header-content">
        <div class="logo-section">
          <div class="logo-icon">
            <span class="icon-pulse">⚡</span>
          </div>
          <h1 class="dashboard-title">FAMS Data Process View</h1>
        </div>
        <div class="header-controls">
          <div class="loader-controls">
            <div class="loader-toggle">
              <label class="toggle-label">
                <input type="checkbox" v-model="showLoaderOption" class="toggle-checkbox">
                <span class="toggle-text">Show Loader</span>
              </label>
            </div>
            <div v-if="showLoaderOption" class="loader-speed">
              <label class="speed-label">
                <span class="speed-text">Speed:</span>
                <select v-model="minLoaderTime" class="speed-select">
                  <option :value="0">Instant</option>
                  <option :value="500">Fast (0.5s)</option>
                  <option :value="1000">Normal (1s)</option>
                  <option :value="1500">Smooth (1.5s)</option>
                  <option :value="2000">Slow (2s)</option>
                </select>
              </label>
            </div>
            <div class="auto-refresh-toggle">
              <label class="toggle-label">
                <input type="checkbox" v-model="autoRefreshEnabled" @change="handleAutoRefreshToggle" class="toggle-checkbox">
                <span class="toggle-text">Auto Refresh</span>
              </label>
            </div>
            <div v-if="autoRefreshEnabled" class="refresh-interval">
              <label class="interval-label">
                <span class="interval-text">Every:</span>
                <select v-model="refreshInterval" @change="handleRefreshIntervalChange" class="interval-select">
                  <option :value="30000">30 sec</option>
                  <option :value="60000">1 min</option>
                  <option :value="300000">5 min</option>
                  <option :value="600000">10 min</option>
                </select>
              </label>
            </div>
          </div>
          <div class="live-indicator">
            <div :class="['live-dot', { 'error': errorCount >= 3, 'auto-refresh': autoRefreshEnabled }]"></div>
            <span style="color: white; font-size: 0.875rem;">
              {{ autoRefreshEnabled ? 'Live (' + formatRefreshInterval() + ')' : connectionStatus }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Global Loader Overlay -->
    <div v-if="showLoaderOption && isLoading" class="global-loader-overlay">
      <div class="global-loader-content">
        <div class="global-loader"></div>
        <div class="loader-text">{{ loaderMessage }}</div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <!-- Statistics Section -->
      <div class="stats-section">
        <div class="stats-header">
          <h3 class="stats-title">Process Log Overview</h3>
          <button class="toggle-stats-btn" @click="statsVisible = !statsVisible">
            <span class="toggle-icon">{{ statsVisible ? '📊' : '📈' }}</span>
            {{ statsVisible ? 'Hide Statistics' : 'Show Statistics' }}
          </button>
        </div>

        <transition name="slide-fade">
          <div v-if="statsVisible" class="stats-wrapper">
            <div class="stats-container">
              <!-- Total Records Card -->
              <div class="stat-card clickable-stat" @click="openProcessBreakdown" title="Click to see process breakdown">
                <div class="stat-label">Total Process Logs</div>
                <div class="stat-value">{{ summaryStats.totalCount || 0 }}</div>
                <div class="stat-change positive">
                  <span class="stat-icon">📝</span>
                  <span>{{ getDateRangeText() }}</span>
                </div>
              </div>

              <!-- Successful Operations Card -->
              <div class="stat-card clickable-stat" @click="filterByStatus('SUCCESS')" title="Click to filter successful">
                <div class="stat-label">Successful Operations</div>
                <div class="stat-value" style="color: #27AE60;">{{ summaryStats.successCount || 0 }}</div>
                <div class="stat-change positive">
                  <span class="stat-icon">✓</span>
                  <span>{{ getSuccessRate() }}% success rate</span>
                </div>
              </div>

              <!-- Failed Operations Card -->
              <div class="stat-card clickable-stat" @click="filterByStatus('ERROR')" title="Click to filter errors">
                <div class="stat-label">Failed Operations</div>
                <div class="stat-value" style="color: #E74C3C;">{{ summaryStats.errorCount || 0 }}</div>
                <div class="stat-change" :class="summaryStats.errorCount > 0 ? 'negative' : 'positive'">
                  <span class="stat-icon">{{ summaryStats.errorCount > 0 ? '⚠️' : '✅' }}</span>
                  <span>{{ summaryStats.errorCount > 0 ? 'Requires attention' : 'No errors' }}</span>
                </div>
              </div>

              <!-- Total Accounts Card -->
              <div class="stat-card clickable-stat" @click="openAccountSummary" title="Click to see account details">
                <div class="stat-label">Total Accounts</div>
                <div class="stat-value">{{ summaryStats.totalAccounts || 0 }}</div>
                <div class="stat-change" :class="unprocessedAccounts.length > 0 ? 'warning' : 'positive'">
                  <span class="stat-icon">👥</span>
                  <span>{{ unprocessedAccounts.length }} unprocessed</span>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- Filters Section -->
      <div class="search-section">
        <div class="filter-header">
          <h3 class="filter-title">Process Log Filters</h3>
          <button class="toggle-filter-btn" @click="filtersVisible = !filtersVisible">
            <span class="toggle-icon">{{ filtersVisible ? '📁' : '📂' }}</span>
            {{ filtersVisible ? 'Hide Filters' : 'Show Filters' }}
          </button>
        </div>

        <transition name="slide-fade">
          <div v-if="filtersVisible" class="search-controls-wrapper">
            <div class="search-controls">
              <div class="search-group date-filter-group">
                <label class="search-label">Date Range <span class="date-range-showing">{{ activeDateRangeLabel }}</span></label>
                <div class="date-quick-buttons">
                  <button v-for="opt in dateRangeOptions" :key="opt.value"
                    :class="['date-quick-btn', { active: selectedDateRange === opt.value }]"
                    @click="selectDateRange(opt.value)">{{ opt.label }}</button>
                </div>
              </div>

              <!-- Process Type Filter -->
              <div class="search-group">
                <label class="search-label">Process Type <span class="multi-badge" v-if="filters.processTypes.length">({{ filters.processTypes.length }})</span></label>
                <div class="multi-select-container">
                  <div class="multi-select-display" @click="toggleDropdown('processType')">
                    <span v-if="filters.processTypes.length === 0" class="placeholder">All Process Types</span>
                    <span v-else class="selected-items">
                      {{ filters.processTypes.length }} selected
                    </span>
                    <span class="dropdown-arrow">▼</span>
                  </div>
                  <div v-if="dropdownStates.processType" class="multi-select-dropdown">
                    <div class="select-all-option">
                      <label class="checkbox-option">
                        <input type="checkbox" @change="toggleAllProcessTypes" :checked="filters.processTypes.length === 0">
                        <span>Select All</span>
                      </label>
                    </div>
                    <div class="dropdown-options">
                      <label class="checkbox-option" v-for="type in processTypeOptions" :key="type.value">
                        <input type="checkbox" :value="type.value" v-model="filters.processTypes" @change="applyFilters">
                        <span>{{ type.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Account Filter -->
              <div class="search-group">
                <label class="search-label">Account ID <span class="multi-badge" v-if="filters.accountIds.length">({{ filters.accountIds.length }})</span></label>
                <div class="multi-select-container">
                  <div class="multi-select-display" @click="toggleDropdown('account')">
                    <span v-if="filters.accountIds.length === 0" class="placeholder">All Accounts</span>
                    <span v-else class="selected-items">
                      {{ filters.accountIds.length }} selected
                    </span>
                    <span class="dropdown-arrow">▼</span>
                  </div>
                  <div v-if="dropdownStates.account" class="multi-select-dropdown">
                    <div class="select-all-option">
                      <label class="checkbox-option">
                        <input type="checkbox" @change="toggleAllAccounts" :checked="filters.accountIds.length === 0">
                        <span>Select All</span>
                      </label>
                    </div>
                    <div class="dropdown-options">
                      <label class="checkbox-option" v-for="accountId in uniqueAccountIds" :key="accountId">
                        <input type="checkbox" :value="accountId" v-model="filters.accountIds" @change="applyFilters">
                        <span>Account {{ accountId }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Status Filter -->
              <div class="search-group">
                <label class="search-label">Status <span class="multi-badge" v-if="filters.statuses.length">({{ filters.statuses.length }})</span></label>
                <div class="multi-select-container">
                  <div class="multi-select-display" @click="toggleDropdown('status')">
                    <span v-if="filters.statuses.length === 0" class="placeholder">All Status</span>
                    <span v-else class="selected-items">
                      {{ filters.statuses.length }} selected
                    </span>
                    <span class="dropdown-arrow">▼</span>
                  </div>
                  <div v-if="dropdownStates.status" class="multi-select-dropdown">
                    <div class="select-all-option">
                      <label class="checkbox-option">
                        <input type="checkbox" @change="toggleAllStatuses" :checked="filters.statuses.length === 0">
                        <span>Select All</span>
                      </label>
                    </div>
                    <div class="dropdown-options">
                      <label class="checkbox-option" v-for="status in statusOptions" :key="status.value">
                        <input type="checkbox" :value="status.value" v-model="filters.statuses" @change="applyFilters">
                        <span>{{ status.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Column Selector -->
              <div class="search-group">
                <label class="search-label">
                  Visible Columns
                  <span class="multi-badge" v-if="selectedColumns.length !== availableColumns.length">
                    ({{ selectedColumns.length }}/{{ availableColumns.length }})
                  </span>
                </label>
                <div class="multi-select-container">
                  <div class="multi-select-display" @click="toggleDropdown('columns')">
                    <span v-if="selectedColumns.length === availableColumns.length" class="placeholder">All Columns</span>
                    <span v-else class="selected-items">
                      {{ selectedColumns.length }} columns selected
                    </span>
                    <span class="dropdown-arrow">▼</span>
                  </div>
                  <div v-if="dropdownStates.columns" class="multi-select-dropdown">
                    <div class="select-all-option">
                      <label class="checkbox-option">
                        <input type="checkbox" @change="toggleAllColumns" :checked="selectedColumns.length === availableColumns.length">
                        <span>Select All</span>
                      </label>
                    </div>
                    <div class="dropdown-options">
                      <label class="checkbox-option" v-for="column in availableColumns" :key="column.key">
                        <input type="checkbox" :value="column.key" v-model="selectedColumns">
                        <span>{{ column.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="filter-actions">
              <button class="apply-filters-btn" @click="applyFilters">
                Apply Filters
              </button>
              <button class="reset-filters-btn" @click="resetFilters">
                Reset
              </button>
              <button v-if="summaryStats.errorCount > 0" class="error-details-btn" @click="openErrorDetails">
                View Errors ({{ summaryStats.errorCount }})
              </button>
            </div>
          </div>
        </transition>
      </div>

      <!-- Data Grid -->
      <div class="grid-container">
        <div class="grid-header">
          <div class="grid-title">
            Process Log Activity
          </div>
          <div class="grid-controls">
            <div class="rows-selector">
              <span class="rows-label">Show:</span>
              <select v-model="itemsPerPage" @change="handleRowsChange" class="rows-select">
                <option :value="10">10 rows</option>
                <option :value="20">20 rows</option>
                <option :value="50">50 rows</option>
                <option :value="100">100 rows</option>
              </select>
            </div>
            <div class="grid-actions">
              <button class="action-button refresh-btn" @click="refreshData" title="Refresh">
                <span class="btn-icon">🔄</span>
                <span>Refresh</span>
              </button>
              <button class="action-button export-btn" @click="exportData" title="Export">
                <span class="btn-icon">📥</span>
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>

        <div class="table-container">
          <div v-if="loading" class="loading-overlay">
            <div class="loader"></div>
          </div>

          <table v-if="!loading && filteredActivities.length > 0" class="data-table">
            <thead>
              <tr>
                <th v-for="column in visibleColumns" :key="column.key">
                  {{ column.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="activity in paginatedActivities" :key="activity.ID"
                  @click="showActivityDetails(activity)"
                  :class="{ 'error-row': activity.Status === 'ERROR' }">

                <td v-if="isColumnVisible('ProcessTime')">
                  <div>{{ formatDateTime(activity.ProcessTime) }}</div>
                  <div style="font-size: 0.75rem; color: #6c757d;">
                    {{ getRelativeTime(activity.ProcessTime) }}
                  </div>
                </td>

                <td v-if="isColumnVisible('AccountID')">
                  <span class="account-id-badge" v-if="activity.AccountID">
                    {{ activity.AccountID }}
                  </span>
                  <span v-else class="system-badge">System</span>
                </td>

                <td v-if="isColumnVisible('ProcessType')">
                  <span :class="['process-badge', getProcessTypeClass(activity.ProcessType)]">
                    {{ activity.ProcessType }}
                  </span>
                </td>

                <td v-if="isColumnVisible('Status')">
                  <div class="status-indicator" :class="'status-' + activity.Status.toLowerCase()">
                    <span class="status-dot"></span>
                    {{ activity.Status }}
                  </div>
                </td>

                <td v-if="isColumnVisible('Message')">
                  <div class="message-cell">
                    {{ activity.Message }}
                  </div>
                </td>

                <td v-if="isColumnVisible('Description')">
                  <div class="description-cell">
                    {{ activity.Description }}
                  </div>
                </td>

                <td v-if="isColumnVisible('ID')">
                  <code style="font-size: 0.75rem;">#{{ activity.ID }}</code>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="!loading && filteredActivities.length === 0" class="empty-state">
            <div class="empty-icon">📂</div>
            <div class="empty-title">No Process Logs Found</div>
            <div class="empty-description">
              Try adjusting your filters or check back later for new process logs
            </div>
          </div>
        </div>

        <div v-if="totalPages > 1" class="pagination-container">
          <div class="pagination-info">
            Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
            {{ Math.min(currentPage * itemsPerPage, filteredActivities.length) }}
            of {{ filteredActivities.length }} entries
          </div>
          <div class="pagination-controls">
            <button
              class="page-btn prev-btn"
              @click="currentPage = 1"
              :disabled="currentPage === 1">
              First
            </button>
            <button
              class="page-btn prev-btn"
              @click="currentPage--"
              :disabled="currentPage === 1">
              Previous
            </button>
            <button
              v-for="page in visiblePageNumbers"
              :key="page"
              class="page-btn page-number"
              :class="{ 'active': page === currentPage }"
              @click="currentPage = page">
              {{ page }}
            </button>
            <button
              class="page-btn next-btn"
              @click="currentPage++"
              :disabled="currentPage === totalPages">
              Next
            </button>
            <button
              class="page-btn next-btn"
              @click="currentPage = totalPages"
              :disabled="currentPage === totalPages">
              Last
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FamsProcessLogView',

  data: function() {
    return {
      // Popup states
      showProcessBreakdown: false,
      showAccountSummary: false,
      showErrorDetails: false,
      selectedActivity: null,
      parsedAccountSummary: [],

      // Loader states
      showLoaderOption: true,
      isLoading: false,
      loaderMessage: 'Loading...',
      loaderTimeout: null,
      minLoaderTime: 1000,
      loaderStartTime: null,

      // Auto-refresh settings
      autoRefreshEnabled: false,
      refreshInterval: 60000,
      refreshTimer: null,

      // Connection states
      connectionStatus: 'Live',
      loading: false,
      lastUpdate: null,
      errorCount: 0,

      // API Configuration
      apiConfig: {
        baseURL: process.env.VUE_APP_API_URL || '',
        accountId: localStorage.getItem('userAccountId')
      },

      // Pagination
      currentPage: 1,
      itemsPerPage: 20,

      // Main data
      activities: [],
      rawApiData: {},

      // Summary data from API - Updated field names
      summaryStats: {
        totalAccounts: 0,
        successCount: 0,
        errorCount: 0,
        totalCount: 0
      },
      processTypeBreakdown: [],
      errorDetails: [],
      unprocessedAccounts: [],
      uniqueAccountIds: [],

      // Filters
      filters: {
        startDateTime: this.getDefaultStartDate(),
        endDateTime: this.getDefaultEndDate(),
        processTypes: [],
        accountIds: [],
        statuses: [],
        specificDate: ''
      },
      selectedDateRange: 'last3days',
      dateRangeOptions: [
        { value: 'last3days', label: 'Last 3 Days' },
        { value: 'today',     label: 'Today' },
        { value: 'yesterday', label: 'Yesterday' },
        { value: '2daysago',  label: '2 Days Ago' }
      ],
      filtersVisible: true,
      statsVisible: true,

      // Dropdown states
      dropdownStates: {
        processType: false,
        account: false,
        status: false,
        columns: false
      },

      // Filter options
      processTypeOptions: [
        { value: 'Android Integrity Check', label: 'Android Integrity Check', color: '#27AE60' },
        { value: 'IOT Integrity Check', label: 'IOT Integrity Check', color: '#3498DB' },
        { value: 'UsageDispensing Update', label: 'Usage Dispensing Update', color: '#9B59B6' }
      ],

      statusOptions: [
        { value: 'SUCCESS', label: 'Success', color: '#27AE60' },
        { value: 'ERROR', label: 'Error', color: '#E74C3C' }
      ],

      // Column management
      availableColumns: [
        { key: 'ProcessTime', label: 'Process Time', visible: true },
        { key: 'AccountID', label: 'Account ID', visible: true },
        { key: 'ProcessType', label: 'Process Type', visible: true },
        { key: 'Status', label: 'Status', visible: true },
        { key: 'Message', label: 'Message', visible: true },
        { key: 'Description', label: 'Description', visible: true },
        { key: 'ID', label: 'Log ID', visible: false }
      ],

      selectedColumns: ['ProcessTime', 'AccountID', 'ProcessType', 'Status', 'Message', 'Description']
    };
  },

  computed: {
    filteredActivities: function() {
      var filtered = this.activities.slice();

      if (this.filters.startDateTime) {
        var startDate = new Date(this.filters.startDateTime);
        filtered = filtered.filter(function(a) {
          return new Date(a.ProcessTime) >= startDate;
        });
      }

      if (this.filters.endDateTime) {
        var endDate = new Date(this.filters.endDateTime);
        filtered = filtered.filter(function(a) {
          return new Date(a.ProcessTime) <= endDate;
        });
      }

      if (this.filters.processTypes.length > 0) {
        var processTypes = this.filters.processTypes;
        filtered = filtered.filter(function(a) {
          return processTypes.indexOf(a.ProcessType) !== -1;
        });
      }

      if (this.filters.accountIds.length > 0) {
        var accountIds = this.filters.accountIds;
        filtered = filtered.filter(function(a) {
          return accountIds.indexOf(a.AccountID) !== -1;
        });
      }

      if (this.filters.statuses.length > 0) {
        var statuses = this.filters.statuses;
        filtered = filtered.filter(function(a) {
          return statuses.indexOf(a.Status) !== -1;
        });
      }

      return filtered;
    },

    totalPages: function() {
      return Math.ceil(this.filteredActivities.length / this.itemsPerPage);
    },

    paginatedActivities: function() {
      var start = (this.currentPage - 1) * this.itemsPerPage;
      var end = start + this.itemsPerPage;
      return this.filteredActivities.slice(start, end);
    },

    visiblePageNumbers: function() {
      var maxVisible = 5;
      var pages = [];
      var start = Math.max(1, this.currentPage - 2);
      var end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (var i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },

    visibleColumns: function() {
      var self = this;
      return this.availableColumns.filter(function(col) {
        return self.selectedColumns.indexOf(col.key) !== -1;
      });
    },

    minAllowedDate: function() {
      var d = new Date();
      d.setDate(d.getDate() - 3);
      return d.toISOString().split('T')[0];
    },

    maxAllowedDate: function() {
      return new Date().toISOString().split('T')[0];
    },

    activeDateRangeLabel: function() {
      if (this.filters.specificDate)
        return 'Showing: ' + this.filters.specificDate + ' (00:00 – 23:59)';
      var opt = this.dateRangeOptions.find(function(o) { return o.value === this.selectedDateRange; }.bind(this));
      return opt ? opt.label : '';
    }
  },

  methods: {
    getDefaultStartDate: function() {
      var date = new Date();
      date.setDate(date.getDate() - 3);
      date.setHours(0, 0, 0, 0);
      return this.formatDateForInput(date);
    },

    getDefaultEndDate: function() {
      var date = new Date();
      date.setHours(23, 59, 59, 999);
      return this.formatDateForInput(date);
    },

    formatDateForInput: function(date) {
      var year = date.getFullYear();
      var month = String(date.getMonth() + 1).padStart(2, '0');
      var day = String(date.getDate()).padStart(2, '0');
      var hours = String(date.getHours()).padStart(2, '0');
      var minutes = String(date.getMinutes()).padStart(2, '0');

      return year + '-' + month + '-' + day + 'T' + hours + ':' + minutes;
    },

    selectDateRange: function(rangeValue) {
      this.selectedDateRange = rangeValue;
      this.filters.specificDate = '';
      var now = new Date();
      var offsets = { last3days: -3, today: 0, yesterday: -1, '2daysago': -2 };
      var offset = offsets[rangeValue];

      var start = new Date(now);
      start.setDate(start.getDate() + (rangeValue === 'last3days' ? -3 : offset));
      start.setHours(0, 0, 0, 0);

      var end = new Date(now);
      if (rangeValue !== 'last3days') end.setDate(end.getDate() + offset);
      end.setHours(23, 59, 59, 999);

      this.filters.startDateTime = this.formatDateForInput(start);
      this.filters.endDateTime   = this.formatDateForInput(end);
      this.applyFilters();
    },

    onSpecificDateChange: function() {
      if (!this.filters.specificDate) return;
      this.selectedDateRange = '';
      var parts = this.filters.specificDate.split('-');
      var y = parts[0];
      var m = parts[1];
      var d = parts[2];
      var start = new Date(y, m - 1, d, 0, 0, 0, 0);
      var end   = new Date(y, m - 1, d, 23, 59, 59, 999);
      this.filters.startDateTime = this.formatDateForInput(start);
      this.filters.endDateTime   = this.formatDateForInput(end);
      this.applyFilters();
    },

  loadProcessLogData: function() {
  var self = this;
  try {
    self.startLoader('Loading Process Log data...');

    self.loading = true;
    self.connectionStatus = 'Loading...';

    var onSuccess = function (response) {
      console.log('API Response received:', response);

      // The response is an array with jsonData property
      if (response.data && response.data.length > 0 && response.data[0].jsonData) {
        // Parse the jsonData string
        var jsonDataString = response.data[0].jsonData;
        console.log('JSON Data String:', jsonDataString);

        try {
          var res = JSON.parse(jsonDataString);
          console.log('Parsed JSON Data:', res);

          self.rawApiData = res;

          // Process main log data
          if (res.processLogData) {
            console.log('Process Log Data found:', res.processLogData);
            self.processApiData(res.processLogData);
          } else {
            console.error('No processLogData in response');
            self.activities = [];
          }

          // Parse summary stats - it's already a string that needs parsing
          if (res.summaryStats) {
            try {
              self.summaryStats = JSON.parse(res.summaryStats);
              console.log('Summary Stats:', self.summaryStats);
            } catch(e) {
              console.error('Error parsing summary stats:', e);
              self.summaryStats = {
                totalAccounts: 0,
                successCount: 0,
                errorCount: 0,
                totalCount: 0
              };
            }
          }

          // Store process type breakdown - this is already an array
          self.processTypeBreakdown = res.processTypeBreakdown || [];
          console.log('Process Type Breakdown:', self.processTypeBreakdown);

          // Store error details if any
          self.errorDetails = res.errorDetails || [];

          // Parse unprocessed accounts if present
          if (res.unprocessedAccounts) {
            try {
              self.unprocessedAccounts = JSON.parse(res.unprocessedAccounts);
            } catch(e) {
              self.unprocessedAccounts = [];
            }
          }

          // Parse account summary
          if (res.accountSummary) {
            try {
              self.parsedAccountSummary = JSON.parse(res.accountSummary);
              console.log('Account Summary:', self.parsedAccountSummary);
            } catch(e) {
              self.parsedAccountSummary = [];
            }
          }

          self.lastUpdate = new Date();
          self.connectionStatus = 'Live';
          self.errorCount = 0;

          var totalRecords = self.summaryStats.totalCount || (res.processLogData && res.processLogData.length) || 0;
          self.showNotification('Success', 'Loaded ' + totalRecords + ' process log entries', 'success');

        } catch (parseError) {
          console.error('Error parsing jsonData:', parseError);
          self.showNotification('Error', 'Failed to parse data from server', 'danger');
        }
      } else {
        console.error('No data or invalid format in response');
        self.activities = [];
        self.showNotification('Warning', 'No data received from server', 'warning');
      }

      self.stopLoader();
    };

    var onError = function(error) {
      console.error('API Error:', error);
      self.loading = false;
      self.stopLoader();
      self.showNotification('Error', 'Failed to load data: ' + (error.message || 'Unknown error'), 'danger');
    };

    var onFinally = function (res) {
      console.log('Process Log data load attempt finished.');
      self.loading = false;
      self.stopLoader();
    };

    // API endpoint
    console.log('Calling API with accountId:', self.apiConfig.accountId);

    self.$ajaxGet(
      self,
      'VwIotdataNotificationComprehensiveActivity/GetIotProcessLogView?accountId=' + self.apiConfig.accountId,
      onSuccess,
      onFinally,
      onError
    );

  } catch (error) {
    console.error('Error in loadProcessLogData:', error);
    this.errorCount++;
    this.connectionStatus = 'Connection Error';

    if (this.errorCount >= 3) {
      this.showNotification('Error', 'Failed to load Process Log data. Retrying...', 'danger');
    }
    this.loading = false;
    this.stopLoader();
  }
},

    processApiData: function(processLogData) {
      console.log('Processing Process Log data:', processLogData);

      var self = this;
      // Map camelCase fields from API to internal format
      this.activities = processLogData.map(function(item) {
        return {
          ID: item.id,
          AccountID: item.accountId,
          ProcessType: item.processType,
          Status: item.status,
          Message: item.message,
          ProcessTime: item.processTime,
          Description: item.description
        };
      });

      // Sort by ProcessTime descending (most recent first)
      this.activities.sort(function(a, b) {
        return new Date(b.ProcessTime) - new Date(a.ProcessTime);
      });

      // Extract unique account IDs
      var accountIds = this.activities
        .map(function(a) { return a.AccountID; })
        .filter(Boolean);

      var uniqueIds = [];
      accountIds.forEach(function(id) {
        if (uniqueIds.indexOf(id) === -1) {
          uniqueIds.push(id);
        }
      });

      this.uniqueAccountIds = uniqueIds.sort(function(a, b) { return a - b; });
    },

    getProcessTypeClass: function(processType) {
      switch(processType) {
        case 'Android Integrity Check':
          return 'process-android';
        case 'IOT Integrity Check':
          return 'process-iot';
        case 'UsageDispensing Update':
          return 'process-usage';
        default:
          return 'process-default';
      }
    },

    getSuccessRate: function() {
      var total = this.summaryStats.totalCount;
      var successful = this.summaryStats.successCount;
      if (total === 0) return 0;
      return ((successful / total) * 100).toFixed(1);
    },

    getDateRangeText: function() {
      if (this.rawApiData && this.rawApiData.startDate && this.rawApiData.endDate) {
        var start = new Date(this.rawApiData.startDate).toLocaleDateString();
        var end = new Date(this.rawApiData.endDate).toLocaleDateString();
        return start + ' - ' + end;
      }
      return 'Last 3 days';
    },

    filterByStatus: function(status) {
      this.filters.statuses = [status];
      this.applyFilters();
      this.showNotification('Info', 'Filtered to ' + status + ' operations', 'primary');
    },

    openProcessBreakdown: function() {
      this.showProcessBreakdown = true;
    },

    openAccountSummary: function() {
      this.showAccountSummary = true;
    },

    openErrorDetails: function() {
      this.showErrorDetails = true;
    },

    showActivityDetails: function(activity) {
      this.selectedActivity = activity;
    },

    toggleDropdown: function(type) {
      var self = this;
      Object.keys(this.dropdownStates).forEach(function(key) {
        if (key !== type) {
          self.dropdownStates[key] = false;
        }
      });
      this.dropdownStates[type] = !this.dropdownStates[type];
    },

    toggleAllProcessTypes: function() {
      var self = this;
      if (this.filters.processTypes.length === 0) {
        this.filters.processTypes = this.processTypeOptions.map(function(opt) {
          return opt.value;
        });
      } else {
        this.filters.processTypes = [];
      }
      this.applyFilters();
    },

    toggleAllAccounts: function() {
      if (this.filters.accountIds.length === 0) {
        this.filters.accountIds = this.uniqueAccountIds.slice();
      } else {
        this.filters.accountIds = [];
      }
      this.applyFilters();
    },

    toggleAllStatuses: function() {
      var self = this;
      if (this.filters.statuses.length === 0) {
        this.filters.statuses = this.statusOptions.map(function(opt) {
          return opt.value;
        });
      } else {
        this.filters.statuses = [];
      }
      this.applyFilters();
    },

    toggleAllColumns: function() {
      var self = this;
      if (this.selectedColumns.length === this.availableColumns.length) {
        this.selectedColumns = ['ProcessTime', 'AccountID', 'ProcessType', 'Status', 'Message'];
      } else {
        this.selectedColumns = this.availableColumns.map(function(col) {
          return col.key;
        });
      }
    },

    isColumnVisible: function(columnKey) {
      return this.selectedColumns.indexOf(columnKey) !== -1;
    },

    applyFilters: function() {
      var self = this;
      this.startLoader('Applying filters...');
      this.currentPage = 1;

      setTimeout(function() {
        self.showNotification('Success', 'Filters applied', 'success');
        self.stopLoader();
      }, 300);
    },

    resetFilters: function() {
      var self = this;
      this.startLoader('Resetting filters...');

      this.filters = {
        startDateTime: this.getDefaultStartDate(),
        endDateTime: this.getDefaultEndDate(),
        processTypes: [],
        accountIds: [],
        statuses: [],
        specificDate: ''
      };
      this.selectedDateRange = 'last3days';

      this.currentPage = 1;

      setTimeout(function() {
        self.showNotification('Success', 'Filters reset', 'success');
        self.stopLoader();
      }, 300);
    },

    refreshData: function() {
      var self = this;
      this.startLoader('Refreshing data...');
      this.showNotification('Info', 'Refreshing data...', 'primary');

      try {
        this.loadProcessLogData();
      } catch (error) {
        console.error('Error refreshing data:', error);
        this.stopLoader();
      }
    },

    exportData: function() {
      var self = this;
      this.startLoader('Exporting data...');

      setTimeout(function() {
        var headers = self.visibleColumns.map(function(col) { return col.label; });

        var csvRows = self.filteredActivities.map(function(activity) {
          return self.visibleColumns.map(function(col) {
            var value = activity[col.key] || '';
            if (col.key === 'ProcessTime') {
              value = self.formatDateTime(value);
            }
            return '"' + value + '"';
          }).join(',');
        });

        var csvContent = [headers.join(',')].concat(csvRows).join('\n');

        var blob = new Blob([csvContent], { type: 'text/csv' });
        var url = window.URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = 'fams_process_log_' + new Date().getTime() + '.csv';
        a.click();
        window.URL.revokeObjectURL(url);

        self.showNotification('Success', 'Data exported successfully', 'success');
        self.stopLoader();
      }, 500);
    },

    formatDateTime: function(date) {
      if (!date) return '';
      var d = new Date(date);

      var year = d.getFullYear();
      var month = String(d.getMonth() + 1).padStart(2, '0');
      var day = String(d.getDate()).padStart(2, '0');
      var hours = String(d.getHours()).padStart(2, '0');
      var minutes = String(d.getMinutes()).padStart(2, '0');
      var seconds = String(d.getSeconds()).padStart(2, '0');

      return year + '/' + month + '/' + day + ' ' + hours + ':' + minutes + ':' + seconds;
    },

    getRelativeTime: function(date) {
      var seconds = Math.floor((new Date() - new Date(date)) / 1000);

      if (seconds < 60) return 'Just now';
      if (seconds < 3600) return Math.floor(seconds / 60) + ' minutes ago';
      if (seconds < 86400) return Math.floor(seconds / 3600) + ' hours ago';
      if (seconds < 604800) return Math.floor(seconds / 86400) + ' days ago';

      return new Date(date).toLocaleDateString();
    },

    handleRowsChange: function() {
      this.currentPage = 1;
      this.showNotification('Success', 'Showing ' + this.itemsPerPage + ' rows per page', 'success');
    },

    startLoader: function(message) {
      var self = this;
      message = message || 'Loading...';

      if (this.showLoaderOption) {
        this.loaderMessage = message;
        this.isLoading = true;
        this.loaderStartTime = Date.now();

        if (this.loaderTimeout) {
          clearTimeout(this.loaderTimeout);
        }

        this.loaderTimeout = setTimeout(function() {
          self.stopLoader(true);
        }, 30000);
      }
    },

    stopLoader: function(forceStop) {
      var self = this;
      var elapsedTime = this.loaderStartTime ? Date.now() - this.loaderStartTime : 0;
      var remainingTime = Math.max(0, this.minLoaderTime - elapsedTime);

      if (forceStop || remainingTime === 0) {
        this.isLoading = false;
        this.loaderMessage = 'Loading...';
        this.loaderStartTime = null;

        if (this.loaderTimeout) {
          clearTimeout(this.loaderTimeout);
          this.loaderTimeout = null;
        }
      } else {
        setTimeout(function() {
          self.isLoading = false;
          self.loaderMessage = 'Loading...';
          self.loaderStartTime = null;

          if (self.loaderTimeout) {
            clearTimeout(self.loaderTimeout);
            self.loaderTimeout = null;
          }
        }, remainingTime);
      }
    },

    handleAutoRefreshToggle: function() {
      if (this.autoRefreshEnabled) {
        this.startAutoRefresh();
        this.showNotification('Success', 'Auto-refresh enabled (every ' + this.formatRefreshInterval() + ')', 'success');
      } else {
        this.stopAutoRefresh();
        this.showNotification('Info', 'Auto-refresh disabled', 'primary');
      }
    },

    handleRefreshIntervalChange: function() {
      if (this.autoRefreshEnabled) {
        this.stopAutoRefresh();
        this.startAutoRefresh();
        this.showNotification('Success', 'Refresh interval changed to ' + this.formatRefreshInterval(), 'success');
      }
    },

    startAutoRefresh: function() {
      var self = this;
      this.stopAutoRefresh();

      if (this.autoRefreshEnabled) {
        this.refreshTimer = setInterval(function() {
          self.loadProcessLogData();
        }, this.refreshInterval);
      }
    },

    stopAutoRefresh: function() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },

    formatRefreshInterval: function() {
      var seconds = this.refreshInterval / 1000;
      if (seconds < 60) {
        return seconds + 's';
      } else {
        var minutes = seconds / 60;
        return minutes < 60 ? minutes + 'm' : (minutes/60) + 'h';
      }
    },

    showNotification: function(title, text, type) {
      type = type || 'primary';
      if (this.$vs && this.$vs.notify) {
        this.$vs.notify({
          title: title,
          text: text,
          color: type,
          position: 'top-right'
        });
      } else {
        console.log(title + ': ' + text);
      }
    },

    handleClickOutside: function(event) {
      var self = this;
      var multiSelectContainers = this.$el.querySelectorAll('.multi-select-container');
      var clickedInside = false;

      multiSelectContainers.forEach(function(container) {
        if (container.contains(event.target)) {
          clickedInside = true;
        }
      });

      if (!clickedInside) {
        Object.keys(this.dropdownStates).forEach(function(key) {
          self.dropdownStates[key] = false;
        });
      }
    }
  },

  mounted: function() {
    var self = this;
    this.loadProcessLogData();

    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    }

    document.addEventListener('click', this.handleClickOutside);

    setTimeout(function() {
      self.showNotification('Welcome!', 'FAMS Data Process View - Ready', 'success');
    }, 500);
  },

  beforeDestroy: function() {
    this.stopAutoRefresh();

    if (this.loaderTimeout) {
      clearTimeout(this.loaderTimeout);
      this.loaderTimeout = null;
    }

    document.removeEventListener('click', this.handleClickOutside);
  }
}
</script>

<style>
/* All CSS remains exactly the same as before - no changes needed */
:root {
  --fams-primary: #2C3E50;
  --fams-secondary: #3498DB;
  --fams-success: #27AE60;
  --fams-warning: #F39C12;
  --fams-danger: #E74C3C;
  --fams-info: #3498DB;

  --autumn-primary: #D2691E;
  --autumn-accent: #FF8C00;

  --gradient-primary: linear-gradient(135deg, var(--autumn-primary) 0%, var(--autumn-accent) 100%);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: #e2e8f0;
  height: 100vh;
  overflow: hidden;
  width: 100%;
}

#app {
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  max-width: none;
}

/* Header */
.header-toolbar {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  padding: 16px 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 20px;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-icon {
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: var(--autumn-primary);
}

.dashboard-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

/* Loader Controls */
.loader-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.toggle-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.toggle-checkbox {
  width: 18px;
  height: 18px;
  margin-right: 8px;
  cursor: pointer;
  accent-color: white;
}

.toggle-text {
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
}

.speed-label, .interval-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.speed-text, .interval-text {
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
}

.speed-select, .interval-select {
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

/* Live Indicator */
.live-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: 20px;
}

.live-dot {
  width: 8px;
  height: 8px;
  background: #4CAF50;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.live-dot.error {
  background: #f44336;
}

.live-dot.auto-refresh {
  background: #4CAF50;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(76, 175, 80, 0); }
  100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
}

/* Global Loader */
.global-loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
}

.global-loader-content {
  background: white;
  border-radius: 16px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.global-loader {
  width: 60px;
  height: 60px;
  border: 4px solid #FFE4B5;
  border-top: 4px solid #D2691E;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loader-text {
  color: #34495E;
  font-size: 1rem;
  font-weight: 500;
  margin-top: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Main Content */
.main-content {
  flex: 1;
  overflow: visible;
  padding: 20px;
  width: 100%;
  background: #e2e8f0;
  position: relative;
}

/* Statistics Section */
.stats-section {
  background: #ffffff;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  overflow: visible;
  width: 100%;
  border: 1px solid #cbd5e1;
}

.stats-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  border-radius: 12px 12px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stats-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
  margin: 0;
}

.toggle-stats-btn, .toggle-filter-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stats-wrapper {
  padding: 16px 20px;
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border-left: 4px solid #D2691E;
  border: 1px solid #e2e8f0;
  position: relative;
  z-index: 1;
  overflow: visible;
}

.clickable-stat {
  cursor: pointer;
}

.clickable-stat:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  background: #ffffff !important;
  border-color: #D2691E;
}

.stat-label {
  color: #2c3e50 !important;
  font-size: 0.875rem;
  margin-bottom: 8px;
  font-weight: 600;
}

.stat-value {
  font-size: 1.75rem;
  color: #2c3e50 !important;
  font-weight: bold;
}

.stat-change {
  font-size: 0.75rem;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-change.positive { color: var(--fams-success); }
.stat-change.negative { color: var(--fams-danger); }
.stat-change.warning { color: var(--fams-warning); }

/* Search/Filter Section */
.search-section {
  background: #ffffff;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  overflow: visible;
  width: 100%;
  border: 1px solid #cbd5e1;
}

.filter-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  border-radius: 12px 12px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
  margin: 0;
}

.search-controls-wrapper {
  padding: 16px 20px;
  position: relative;
  overflow: visible;
}

.search-controls {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
  overflow: visible;
  position: relative;
}

.search-group {
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: visible;
}

.search-label {
  font-size: 0.875rem;
  color: #34495E;
  margin-bottom: 4px;
  font-weight: 500;
}

.search-input {
  padding: 10px 12px;
  border: 1px solid #94a3b8;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: #ffffff;
}

.search-input:focus {
  outline: none;
  border-color: #D2691E;
  box-shadow: 0 0 0 3px rgba(210, 105, 30, 0.15);
}

/* Multi-select Dropdown */
.multi-select-container {
  position: relative;
  overflow: visible;
}

.multi-select-display {
  padding: 10px 12px;
  border: 1px solid #94a3b8;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  min-height: 44px;
}

.multi-select-display:hover {
  border-color: #D2691E;
  background: #fff8f3;
}

.placeholder {
  color: #999;
}

.selected-items {
  color: #495057;
  font-weight: 500;
}

.dropdown-arrow {
  color: #999;
  font-size: 0.75rem;
  transition: transform 0.3s ease;
}

.multi-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #D2691E;
  border-radius: 8px;
  margin-top: 4px;
  max-height: 250px;
  overflow-y: auto;
  z-index: 9999;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.select-all-option {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  background: #f8f9fa;
}

.dropdown-options {
  max-height: 200px;
  overflow-y: auto;
}

.checkbox-option {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.checkbox-option:hover {
  background: #fff3e0;
}

.checkbox-option input[type="checkbox"] {
  margin-right: 8px;
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #D2691E;
}

.multi-badge {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 4px;
}

/* Filter Actions */
.filter-actions {
  display: flex;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
  position: relative;
  z-index: 1;
}

.apply-filters-btn, .reset-filters-btn, .error-details-btn {
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.apply-filters-btn {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
}

.reset-filters-btn {
  background: white;
  color: #34495E;
  border: 2px solid #FFE4B5;
}

.error-details-btn {
  background: linear-gradient(135deg, #E74C3C 0%, #C0392B 100%);
  color: white;
}

/* Grid Container */
.grid-container {
  background: #ffffff;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  width: 100%;
  border: 1px solid #cbd5e1;
}

.grid-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.grid-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
}

.grid-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.rows-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rows-label {
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 500;
}

.rows-select {
  padding: 6px 12px;
  border: 1px solid #94a3b8;
  border-radius: 6px;
  background: white;
  color: #475569;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 100px;
}

.grid-actions {
  display: flex;
  gap: 8px;
}

.action-button {
  padding: 8px 14px;
  background: white;
  border: 1px solid #94a3b8;
  border-radius: 6px;
  color: #475569;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.action-button:hover {
  background: #f8fafc;
  border-color: #D2691E;
  color: #D2691E;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.btn-icon {
  font-size: 1rem;
}

/* Table */
.table-container {
  overflow: auto;
  position: relative;
  max-height: 500px;
  width: 100%;
  background: #ffffff;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  position: sticky;
  top: 0;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  z-index: 10;
}

.data-table th {
  padding: 12px 16px;
  text-align: left;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.data-table tbody tr {
  border-bottom: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  background: #ffffff;
  cursor: pointer;
}

.data-table tbody tr:hover {
  background: #f1f5f9;
}

.data-table tbody tr.error-row {
  background: #fef2f2;
}

.data-table tbody tr.error-row:hover {
  background: #fee2e2;
}

.data-table tbody tr:nth-child(even) {
  background: #f8fafc;
}

.data-table tbody tr:nth-child(even):hover {
  background: #f1f5f9;
}

.data-table td {
  padding: 12px 16px;
  font-size: 0.875rem;
  color: var(--fams-dark);
}

/* Process Type Badges */
.process-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: none;
  white-space: nowrap;
}

.process-android {
  background: linear-gradient(135deg, #27AE60, #229954);
  color: white;
}

.process-iot {
  background: linear-gradient(135deg, #3498DB, #2980B9);
  color: white;
}

.process-usage {
  background: linear-gradient(135deg, #9B59B6, #8E44AD);
  color: white;
}

.process-default {
  background: linear-gradient(135deg, #95A5A6, #7F8C8D);
  color: white;
}

/* Status Indicators */
.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-success {
  background: rgba(39, 174, 96, 0.1);
  color: #27AE60;
}

.status-error {
  background: rgba(231, 76, 60, 0.1);
  color: #E74C3C;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

/* Account and System Badges */
.account-id-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #f1f5f9;
  border-radius: 4px;
  font-weight: 600;
  color: #475569;
  font-family: monospace;
}

.system-badge {
  display: inline-block;
  padding: 2px 8px;
  background: linear-gradient(135deg, #34495E, #2C3E50);
  color: white;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.75rem;
}

/* Message and Description Cells */
.message-cell {
  min-width: 250px;
  max-width: 500px;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  line-height: 1.4;
  padding: 4px 0;
}

.description-cell {
  color: #64748b;
  font-size: 0.875rem;
  font-style: italic;
  min-width: 200px;
  max-width: 400px;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--fams-dark);
  background: #f8fafc;
  border-radius: 8px;
  margin: 20px;
  border: 1px solid #e2e8f0;
}

.empty-icon {
  font-size: 4rem;
  color: var(--autumn-light);
  margin-bottom: 16px;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.empty-description {
  color: #6c757d;
  text-align: center;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(241, 245, 249, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.loader {
  width: 48px;
  height: 48px;
  border: 4px solid var(--autumn-light);
  border-top: 4px solid var(--autumn-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Pagination */
.pagination-container {
  padding: 16px 20px;
  border-top: 1px solid #cbd5e1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f1f5f9;
}

.pagination-info {
  color: #6c757d;
  font-size: 0.875rem;
}

.pagination-controls {
  display: flex;
  gap: 4px;
}

.page-btn {
  padding: 6px 12px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  color: #495057;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  min-width: 40px;
}

.page-btn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #D2691E;
  color: #D2691E;
}

.page-btn.active {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border-color: #D2691E;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Popup Styles */
.simple-popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.popup-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 700px;
  max-height: 80vh;
  overflow: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border-radius: 8px 8px 0 0;
}

.popup-header h3 {
  margin: 0;
  font-size: 1.25rem;
}

.popup-header h4 {
  margin: 0 0 12px 0;
  color: #334155;
  font-size: 1rem;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 28px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.popup-body {
  padding: 20px;
}

.detail-row {
  margin-bottom: 12px;
  font-size: 0.9rem;
}

.detail-row strong {
  color: #34495E;
  display: inline-block;
  min-width: 120px;
}

.message-text {
  margin-top: 8px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
  line-height: 1.5;
  word-wrap: break-word;
}

/* Process Breakdown Popup */
.process-breakdown-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.process-item {
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e2e8f0;
}

.process-header {
  margin-bottom: 12px;
}

.process-stats {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.stat-value.success {
  color: #27AE60;
}

.stat-value.error {
  color: #E74C3C;
}

.process-bar {
  width: 100%;
  height: 20px;
  background: #fee2e2;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 8px;
}

.success-bar {
  height: 100%;
  background: linear-gradient(90deg, #27AE60 0%, #229954 100%);
  transition: width 0.3s ease;
}

/* Account Summary Popup */
.account-summary-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.summary-section h4 {
  margin: 0 0 12px 0;
  color: #334155;
  font-size: 1rem;
}

.account-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.account-card {
  background: #f8fafc;
  border-radius: 6px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s;
}

.account-card:hover {
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.account-id {
  font-weight: 600;
  color: #D2691E;
  margin-bottom: 4px;
}

.account-ops {
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 4px;
}

.account-time {
  font-size: 0.75rem;
  color: #94a3b8;
}

.unprocessed-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.unprocessed-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #fff8f3;
  border: 1px solid #fed7aa;
  border-radius: 6px;
}

.account-badge {
  font-weight: 600;
  color: #92400e;
}

.device-count {
  font-size: 0.875rem;
  color: #78716c;
}

/* Error Details Popup */
.error-details-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
}

.error-item {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 12px;
}

.error-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.error-id {
  font-weight: 600;
  color: #991b1b;
  font-size: 0.875rem;
}

.error-account {
  background: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #7f1d1d;
}

.error-process {
  font-weight: 500;
  color: #b91c1c;
  margin-bottom: 8px;
  font-size: 0.9rem;
}

.error-message {
  color: #991b1b;
  background: white;
  padding: 8px;
  border-radius: 4px;
  margin-bottom: 8px;
  font-size: 0.875rem;
  font-family: monospace;
}

.error-time {
  font-size: 0.75rem;
  color: #dc2626;
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease;
  overflow: visible;
}

.slide-fade-leave-active {
  transition: all 0.3s ease;
  overflow: visible;
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .main-content {
    padding: 12px;
  }

  .stats-container {
    grid-template-columns: 1fr 1fr;
  }

  .search-controls {
    grid-template-columns: 1fr;
  }

  .data-table {
    font-size: 0.75rem;
  }

  .data-table th, .data-table td {
    padding: 8px;
  }

  .process-badge {
    font-size: 0.625rem;
    padding: 2px 6px;
  }

  .header-controls {
    flex-wrap: wrap;
  }

  .grid-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .grid-actions {
    justify-content: space-between;
  }

  .message-cell {
    min-width: 150px;
    max-width: 250px;
    font-size: 0.75rem;
  }
}

@media (max-width: 480px) {
  .header-content {
    flex-direction: column;
    gap: 15px;
  }

  .loader-controls {
    flex-wrap: wrap;
    gap: 10px;
  }

  .stats-container {
    grid-template-columns: 1fr;
  }

  .pagination-container {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .pagination-controls {
    justify-content: center;
  }

  .page-btn {
    min-width: 30px;
    padding: 4px 8px;
    font-size: 0.75rem;
  }

  .message-cell {
    min-width: 100px;
    max-width: 200px;
  }
}

@media (min-width: 1400px) {
  .message-cell {
    max-width: 600px;
  }
}

@media (min-width: 2000px) {
  .header-content {
    max-width: 1920px;
    margin: 0 auto;
  }

  .main-content {
    max-width: 1920px;
    margin: 0 auto;
  }
}

/* Date Range Filter Styling */
.date-filter-group {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 8px 6px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
}

.date-range-showing {
  font-size: 0.75rem;
  font-weight: 400;
  color: #64748b;
  text-transform: none;
  letter-spacing: 0;
  display: inline-block;
  margin-left: 6px;
  font-weight: normal;
}

.date-quick-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.date-quick-btn {
  padding: 5px 10px;
  border: 1px solid #cbd5e1;
  background: white;
  border-radius: 5px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.date-quick-btn:hover {
  border-color: #D2691E;
  color: #D2691E;
  background: #fff9f5;
}

.date-quick-btn.active {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border-color: #D2691E;
  box-shadow: 0 2px 8px rgba(210, 105, 30, 0.2);
}
</style>
